import React, {useCallback, useEffect, useState} from 'react';
import {App, Spin} from 'antd';
import {executeDcaReport, getDcaExecutionByReport} from '../../../../services/dcaService';
import type {DcaExecution} from '../../../../types/dca';

/** 币量格式化：≥1 保留 4 位小数，<1 保留 4 位有效数字 */
const formatQty = (qty?: number): string =>
    null == qty ? '--' : (Number(qty) >= 1 ? Number(qty).toFixed(4) : Number(qty).toPrecision(4));

/** 价格格式化（千分位，最多 8 位小数） */
const formatPrice = (price?: number): string =>
    null == price ? '--' : `$${Number(price).toLocaleString('en-US', {maximumFractionDigits: 8})}`;

/** 实际成交金额（USDT = 均价 × 币量，最多 2 位小数） */
const filledUsdt = (execution?: DcaExecution | null): string =>
    (null == execution?.price || null == execution?.qty)
        ? '--'
        : (Number(execution.price) * Number(execution.qty)).toFixed(2);

/**
 * 研报内嵌 DCA 交易条（仅执行按钮 + 状态）：
 * 固定金额市价买入现货；仅看多方向 + 最新研报 + 生成窗口内 + 该研报未执行过时才呈现按钮，
 * 否则只显示不可执行原因文本（不呈现买入入口）。
 * 每份研报仅一次（点过一次无论成败即锁定，可执行性由后端判定，前端仅渲染按钮与原因）。
 */
export const DcaSummaryBar: React.FC<{ reportId?: number }> = ({reportId}) => {
    const {message} = App.useApp();
    const [execution, setExecution] = useState<DcaExecution | null>(null);
    const [loading, setLoading] = useState(false);
    const [executing, setExecuting] = useState(false);

    const load = useCallback(async () => {
        if (null == reportId) {
            return;
        }
        setLoading(true);
        try {
            setExecution(await getDcaExecutionByReport(reportId));
        } catch {
            setExecution(null);
        } finally {
            setLoading(false);
        }
    }, [reportId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleExecute = async () => {
        if (null == reportId || executing) {
            return;
        }
        setExecuting(true);
        try {
            await executeDcaReport(reportId);
            message.success('DCA 买入已成交');
        } catch (e: any) {
            message.error(e?.message ?? 'DCA 执行失败');
        } finally {
            // 无论成败都刷新：失败也锁定（展示失败原因）
            await load();
            setExecuting(false);
        }
    };

    if (loading && null == execution) {
        return <div className="rr-dca-bar"><Spin size="small"/></div>;
    }
    if (null == execution) {
        return <div className="rr-dca-bar rr-dca-dim">DCA 状态加载失败</div>;
    }

    const symbol = execution.symbol ?? '';
    const amountUsdt = null == execution.amountUsdt ? 50 : Number(execution.amountUsdt);
    const status = execution.execStatus;
    const executable = 1 === Number(execution.executable);

    return (
        <div className="rr-dca-bar">
            <span className="rr-dca-tag">DCA交易</span>
            <span className="rr-dca-desc">{symbol} · 市价买入 {amountUsdt}U 现货</span>
            <span className="rr-dca-sep">·</span>
            {renderStatus(execution, status)}
            {null == status ? (
                executable ? (
                    <button className="rr-dca-exec-btn" disabled={executing} onClick={handleExecute}>
                        {executing ? '提交中…' : '执行 DCA'}
                    </button>
                ) : (
                    <span className="rr-dca-exec-disabled">{execution.executableReason ?? '当前不可执行'}</span>
                )
            ) : null}
        </div>
    );
};

/** 执行状态渲染：执行中 / 已成交（价量时间）/ 失败原因 */
function renderStatus(execution: DcaExecution, status?: string) {
    if ('EXECUTING' === status) {
        return <span className="rr-dca-status">执行中…</span>;
    }
    if ('SUCCESS' === status) {
        return (
            <span className="rr-dca-filled">
                已买入 {formatQty(execution.qty)} {execution.symbol} @{formatPrice(execution.price)}
                （{filledUsdt(execution)} USDT）
                <span className="rr-dca-done">✓ 已完成{execution.updatedAt ? ` · ${execution.updatedAt}` : ''}</span>
            </span>
        );
    }
    if ('FAILED' === status) {
        return <span className="rr-dca-failed">✗ 执行失败：{execution.errorMsg ?? '--'}（已锁定，等下一份研报）</span>;
    }
    return null;
}

export default DcaSummaryBar;
